<script>
// Hand-drawn music-notation glyph for the Div knob (MDL-288, spec 131661837 §5).
//
// Composed from simple SVG primitives — notehead (filled/open), stem, flags,
// augmentation dot, and a triplet "3" marker — so the eleven note-value
// durations render without a music font (phone browsers make SMuFL glyph
// rendering a gamble). Everything is drawn in `currentColor` so the glyph
// themes with the surrounding pedal in light and dark.
export default {
  name: 'NoteGlyph',

  props: {
    // 'filled' (crotchet/quaver/…) or 'open' (minim/semibreve).
    notehead: {
      type: String,
      default: 'filled',
      validator: v => v === 'filled' || v === 'open',
    },
    // Whether the note has a stem (false only for the semibreve).
    stem: { type: Boolean, default: true },
    // Number of flags on the stem: 0 crotchet, 1 quaver, 2 semiquaver.
    flags: { type: Number, default: 0 },
    // Augmentation dot (dotted quaver / crotchet / minim).
    dot: { type: Boolean, default: false },
    // Triplet marker — renders a small italic "3" above the note.
    triplet: { type: Boolean, default: false },
    // Accessible label; when empty the glyph is decorative (aria-hidden).
    title: { type: String, default: '' },
  },

  computed: {
    isOpen() { return this.notehead === 'open'; },
    // Flag anchor points down the top of the stem.
    flagOffsets() {
      return Array.from({ length: Math.max(0, this.flags) }, (_, i) => 5 + i * 3.6);
    },
  },
};
</script>

<template>
  <svg
    class="note-glyph"
    viewBox="0 0 24 28"
    fill="currentColor"
    :role="title ? 'img' : undefined"
    :aria-label="title || undefined"
    :aria-hidden="title ? undefined : 'true'"
  >
    <!-- Triplet marker -->
    <text
      v-if="triplet"
      class="note-glyph__triplet"
      x="3"
      y="6"
      font-size="9"
      font-style="italic"
      fill="currentColor"
    >3</text>

    <!-- Stem -->
    <rect
      v-if="stem"
      class="note-glyph__stem"
      x="11.3"
      y="6"
      width="1.3"
      height="14"
    />

    <!-- Flags (hang off the top-right of the stem) -->
    <path
      v-for="(y, i) in flagOffsets"
      :key="i"
      class="note-glyph__flag"
      :d="`M12.6 ${y} q5.4 1.4 4.6 6.6 q-0.4 -3.6 -4.6 -4.6 z`"
    />

    <!-- Notehead (angled oval) -->
    <g transform="rotate(-24 8 20)">
      <ellipse v-if="!isOpen" cx="8" cy="20" rx="4.5" ry="3.1" />
      <ellipse
        v-else
        cx="8"
        cy="20"
        rx="4.5"
        ry="3.1"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
      />
    </g>

    <!-- Augmentation dot -->
    <circle v-if="dot" class="note-glyph__dot" cx="15" cy="20" r="1.4" />
  </svg>
</template>

<style scoped>
.note-glyph {
  display: inline-block;
  width: 1.15em;
  height: 1.35em;
  vertical-align: middle;
  overflow: visible;
}
</style>
